import type { DatasetSource } from 'src/components/star-grid/datasets/datasetTypes';

/** Tables listed per page of the source picker. */
export const SOURCE_PAGE_SIZE = 50;

/** Every source whose table or ref contains the search text (case-insensitive); all of them when the search is empty. */
export function matchingSources(sources: readonly DatasetSource[], search: string): DatasetSource[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...sources];
  return sources.filter((source) => source.table.toLowerCase().includes(needle) || source.ref.toLowerCase().includes(needle));
}

export interface SourcePage {
  readonly shown: DatasetSource[];
  readonly total: number;
  readonly pagesCount: number;
  /** The 1-based page shown, clamped to the pages there are. */
  readonly pageIndex: number;
}

/** One page of the matches, so every table is reachable however long the catalog grows. */
export function pageSources(sources: readonly DatasetSource[], search: string, pageIndex: number): SourcePage {
  const matches = matchingSources(sources, search);
  const pagesCount = Math.max(1, Math.ceil(matches.length / SOURCE_PAGE_SIZE));
  const page = Math.min(Math.max(1, pageIndex), pagesCount);
  const start = (page - 1) * SOURCE_PAGE_SIZE;
  return { shown: matches.slice(start, start + SOURCE_PAGE_SIZE), total: matches.length, pagesCount, pageIndex: page };
}

/** Where the Source step picks from: a catalog (Glue) table, or a typed S3 path. */
export type SourceMode = 'catalog' | 'uri';

/** The `catalog` the service gives S3 entries in the source list. */
const S3_SOURCE_CATALOG = 's3';

/** The catalog tables only: S3 is entered as a typed path, so S3 entries are not listed as tables. */
export function catalogSources(sources: readonly DatasetSource[]): DatasetSource[] {
  return sources.filter((source) => source.catalog !== S3_SOURCE_CATALOG);
}

/** `s3://bucket` with any folder under it, the bucket root included; the service validates the rest. */
export function isS3Uri(value: string): boolean {
  return /^s3:\/\/[^/\s]+(\/.*)?$/.test(value.trim());
}
